import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, Facebook, Instagram, Twitter, Mail, Phone, MessageCircle,
  Home, Info, HelpCircle, Shield, FileText, CreditCard, Crown, Settings,
  User, Star, Gift, Calendar, MapPin, Image, Users, Search, Bookmark, 
  Bell, HandHeart, Link as LinkIcon
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Icon mapping
const ICON_MAP = {
  Home, Info, HelpCircle, Shield, FileText, Mail, Phone, CreditCard, Crown,
  Heart, Star, Gift, User, Users, Settings, Search, Bookmark, Bell, Calendar,
  MapPin, Image, HandHeart, MessageCircle, Link: LinkIcon
};

const getIcon = (iconName) => {
  if (!iconName || iconName === 'none') return null;
  return ICON_MAP[iconName] || null;
};

function Footer() {
  const [footerSettings, setFooterSettings] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const { theme } = useTheme();

  const loadSettings = async () => {
    try {
      const [footerRes, siteRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/footer-settings/public`),
        fetch(`${API_URL}/api/admin/site-settings/public`)
      ]);

      if (footerRes.ok) {
        const footerData = await footerRes.json();
        setFooterSettings(footerData);
      }

      if (siteRes.ok) {
        const siteData = await siteRes.json();
        setSiteSettings(siteData);
      }
    } catch (error) {
      console.error('Failed to load footer settings:', error);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Use theme colors from Website Designer (ThemeContext)
  const bgColor = theme?.footerBackground || '#1f2937';
  const textColor = theme?.footerText || '#ffffff';
  const copyrightText = footerSettings?.copyright_text || '© 2025 qbool. All rights reserved.';
  const showSocialLinks = footerSettings?.show_social_links !== false;
  const showContactInfo = footerSettings?.show_contact_info !== false;
  const columns = footerSettings?.columns || [];

  return (
    <footer style={{ background: bgColor, color: textColor }}>
      <div className="w-full px-4 md:px-8 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                {siteSettings?.logo_url ? (
                  <img src={siteSettings.logo_url} alt={siteSettings.site_name || 'Logo'} className="h-10 object-contain" />
                ) : (
                  <>
                    <Heart className="h-8 w-8 text-mint-500" />
                    <span className="text-2xl font-bold">{siteSettings?.site_name || 'qbool'}</span>
                  </>
                )}
              </div>
              <p className="opacity-70 mb-4 text-sm">
                {siteSettings?.site_tagline || 'A trusted Islamic matrimonial service connecting Muslims worldwide.'}
              </p>

              {/* Social Links */}
              {showSocialLinks && (
                <div className="flex space-x-4">
                  {siteSettings?.facebook_url && (
                    <a 
                      href={siteSettings.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      aria-label="Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                  )}
                  {siteSettings?.instagram_url && (
                    <a 
                      href={siteSettings.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      aria-label="Instagram"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                  )}
                  {siteSettings?.twitter_url && (
                    <a 
                      href={siteSettings.twitter_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      aria-label="Twitter"
                    >
                      <Twitter className="h-5 w-5" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Dynamic Columns from Admin */}
            {columns.length > 0 ? (
              columns.map((column) => (
                <div key={column.id}>
                  <h3 
                    className="text-lg font-semibold mb-4"
                    style={{ color: column.titleColor || footerSettings?.text_color || '#ffffff' }}
                  >
                    {column.title}
                  </h3>
                  <ul className="space-y-2">
                    {column.links?.map((link) => {
                      const IconComponent = getIcon(link.icon);
                      const iconStyle = link.iconColor ? { color: link.iconColor } : {};
                      const linkStyle = { color: column.linkColor || '#9ca3af' };
                      return (
                        <li key={link.id}>
                          {link.url.startsWith('http') ? (
                            <a 
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:opacity-100 transition-opacity text-sm flex items-center gap-2"
                              style={linkStyle}
                            >
                              {IconComponent && <IconComponent className="w-4 h-4" style={iconStyle} />}
                              {link.label}
                            </a>
                          ) : (
                            <Link 
                              to={link.url}
                              className="hover:opacity-100 transition-opacity text-sm flex items-center gap-2"
                              style={linkStyle}
                            >
                              {IconComponent && <IconComponent className="w-4 h-4" style={iconStyle} />}
                              {link.label}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            ) : (
              <>
                {/* Default Quick Links */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                  <ul className="space-y-2">
                    <li><Link to="/about" className="opacity-70 hover:opacity-100 text-sm">About Us</Link></li>
                    <li><Link to="/pricing" className="opacity-70 hover:opacity-100 text-sm">Pricing</Link></li>
                    <li><Link to="/help" className="opacity-70 hover:opacity-100 text-sm">Help Center</Link></li>
                  </ul>
                </div>

                {/* Default Legal */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Legal</h3>
                  <ul className="space-y-2">
                    <li><Link to="/privacy" className="opacity-70 hover:opacity-100 text-sm">Privacy Policy</Link></li>
                    <li><Link to="/terms" className="opacity-70 hover:opacity-100 text-sm">Terms of Service</Link></li>
                    <li><Link to="/contact" className="opacity-70 hover:opacity-100 text-sm">Contact Us</Link></li>
                  </ul>
                </div>
              </>
            )}

            {/* Contact Info */}
            {showContactInfo && (siteSettings?.contact_email || siteSettings?.contact_phone || siteSettings?.whatsapp_number) && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Contact</h3>
                <ul className="space-y-3">
                  {siteSettings?.contact_email && (
                    <li>
                      <a 
                        href={`mailto:${siteSettings.contact_email}`}
                        className="opacity-70 hover:opacity-100 text-sm flex items-center gap-2"
                      >
                        <Mail className="h-4 w-4" />
                        {siteSettings.contact_email}
                      </a>
                    </li>
                  )}
                  {siteSettings?.contact_phone && (
                    <li>
                      <a 
                        href={`tel:${siteSettings.contact_phone}`}
                        className="opacity-70 hover:opacity-100 text-sm flex items-center gap-2"
                      >
                        <Phone className="h-4 w-4" />
                        {siteSettings.contact_phone}
                      </a>
                    </li>
                  )}
                  {siteSettings?.whatsapp_number && (
                    <li>
                      <a 
                        href={`https://wa.me/${siteSettings.whatsapp_number.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="opacity-70 hover:opacity-100 text-sm flex items-center gap-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t border-white/20 text-center opacity-60 text-sm">
            <p>{copyrightText}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
